import * as React from 'react';
import { mergeStyles, IStyle, KeyCodes, getId } from '@fluentui/react';
import { IFluidObject } from '@fluidframework/core-interfaces';
import { ComponentUXContext } from './ComponentUXContextProvider';
import { ComponentFocusable, LayoutMode } from '@fluidx/office-fluid-types';
import { handleTabOnUXElements } from './helpers';

/**
 * Callback that returns component hosting div to a host
 * This is called by the component part signalling that it's ready to render a hosted component
 * within this div it provides
 */
export type ReturnHostingDiv = (hostingDiv: HTMLDivElement) => void;

export interface ComponentPartProps {
  returnHostingDiv: ReturnHostingDiv;

  layoutMode: LayoutMode;

  /** Custom styling for the component part */
  style?: ComponentPartStyles;
}

export interface ComponentPartStyles {
  /** Custom styling to be applied to the root element */
  root?: IStyle;

  /** Custom styling to be applied to the hosting div element */
  hostingDiv?: IStyle;
}

export const componentPartTestId = 'componentPartTestId';
export const componentPartHostingElementTestId = 'componentPartHostingElementTestId';

export const ComponentPart = React.memo((props: ComponentPartProps) => {
  const componentHostingDivRef = React.createRef<HTMLDivElement>();
  const { returnHostingDiv, layoutMode, style } = props;

  const componentHostingDivId = React.useRef<string>(getId('componentPartHostingElementId'));

  const { component, stringTable } = React.useContext(ComponentUXContext)!;

  React.useEffect(() => {
    if (componentHostingDivRef.current) {
      returnHostingDiv(componentHostingDivRef.current);
    }
    // TODO: Review intent. This approach should probably be changed:
    // Using ref.current with useEffect isn't safe as it won't trigger a rerender: https://stackoverflow.com/a/60476525
    // Recommended approach: https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentHostingDivRef.current]);

  React.useEffect(() => {
    if (component?.ComponentFocusable?.setHostComponentFocusable) {
      component.ComponentFocusable.setHostComponentFocusable(getHostComponentFocusable(componentHostingDivRef.current));
    }
  }, [component, componentHostingDivRef]);

  const getHostComponentFocusable = (hostingElement: HTMLDivElement | null) => {
    const hostComponent: ComponentFocusable = {
      get ComponentFocusable(): ComponentFocusable {
        return this;
      },
      giveFocus: () => {
        if (!hostingElement) {
          return false;
        }
        hostingElement.focus();
        return true;
      },
      isComponentFocused: () => {
        // Return true if focus is inside component hosting div
        return hostingElement?.contains(document.activeElement) || false;
      }
    };
    return hostComponent;
  };

  const onComponentHostingElementKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.keyCode) {
      case KeyCodes.enter:
        if (event.target === event.currentTarget && component?.ComponentFocusable) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          tryFocusComponent();
        }
        event.preventDefault();
        // Stop propagation to prevent key from being handled by wrapperElement
        event.stopPropagation();
        break;

      case KeyCodes.escape:
        // To handle escape on non-iframed components, if event is bubbled from
        // a component and it was not handled focus the hosting element
        if (event.target !== event.currentTarget && !event.isDefaultPrevented()) {
          componentHostingDivRef.current?.focus();
          event.stopPropagation();
        }
        break;

      case KeyCodes.tab:
        handleTabOnUXElements(event);
    }
  };

  const tryFocusComponent = async () => {
    const componentFocusable =
      (await component?.ComponentCanvasProvider?.getCanvasComponent())?.ComponentFocusable ||
      component?.ComponentFocusable;

    if (componentFocusable) {
      componentFocusable.giveFocus();
    }
  };

  return (
    <div className={getComponentPartClass(layoutMode, component, style?.root)} data-testid={componentPartTestId}>
      <div
        id={componentHostingDivId.current}
        className={getComponentHostingDivClass(layoutMode, style?.hostingDiv)}
        data-testid={componentPartHostingElementTestId}
        ref={componentHostingDivRef}
        tabIndex={component?.ComponentFocusable ? 0 : -1}
        // eslint-disable-next-line react/jsx-no-bind
        onKeyDown={onComponentHostingElementKeyDown}
        aria-label={
          component?.ComponentFocusable ? stringTable.focusableComponentAriaLabel : stringTable.componentAriaLabel
        }
      />
    </div>
  );
});

const getComponentPartClass = (_layoutMode: LayoutMode, component?: IFluidObject, style?: IStyle) => {
  const baseStyle: IStyle = {
    position: 'inherit',
    overflow: 'hidden'
  };

  // Without component, Let the component part try to display
  // but make sure the user doesn't see it
  const defaultStyle: IStyle = component
    ? {
        display: 'flex',
        flexGrow: 1,
        height: ''
      }
    : {
        height: '0px'
      };

  return mergeStyles(baseStyle, defaultStyle, style);
};

const getComponentHostingDivClass = (layoutMode: LayoutMode, style?: IStyle) => {
  // To get the correct behavior out of the loading component, the actual
  // hosting element needs to have the same layout mode as what the host
  // gave the Component Ux.  While loading we disconnect the layout of this
  // element from the rest of the Component Ux layout, let load happen, then
  // return it to the normal layout.

  const baseStyle: IStyle = {
    position: 'relative',
    maxWidth: '100%',
    overflow: 'hidden'
  };

  let layoutStyle: IStyle = {};

  switch (layoutMode) {
    case LayoutMode.fixedByHost:
      layoutStyle = {
        flexGrow: 1,
        minHeight: '1px'
      };
      break;
    case LayoutMode.growHeight:
      layoutStyle = {
        flexGrow: 1
      };
      break;
    case LayoutMode.grow:
      layoutStyle = {
        display: 'inline-block'
      };
  }

  return mergeStyles(baseStyle, layoutStyle, style);
};

export type LoadableComponentPartProps = Omit<ComponentPartProps, 'returnHostingDiv'>;

/**
 * Light wrapper around ComponentPart. It scaffolds ComponentPart and
 * renders provided component when hosting div is ready.
 *
 * Use this part when you already loaded a component
 * without a hosting div available (load before render)
 * Presently, this is only possible in non-iframe case.
 */
export const LoadableComponentPart = React.memo((props: LoadableComponentPartProps) => {
  const { component } = React.useContext(ComponentUXContext)!;

  const [hostingDiv, setHostingDiv] = React.useState<HTMLDivElement>();

  const returnHostingDiv = React.useCallback((div: HTMLDivElement) => {
    setHostingDiv(div);
  }, []);

  React.useEffect(() => {
    if (component && hostingDiv) {
      const renderable = component.IFluidHTMLView ?? (component as IFluidObject).IFluidHTMLView;

      renderable?.render(hostingDiv);
    }
  }, [component, hostingDiv]);

  return <ComponentPart {...props} returnHostingDiv={returnHostingDiv} />;
});

// cspell:ignore iframed theming testid
